import React, { useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { RUTA_MASCOTAS, RUTA_PROPIETARIOS } from "../../../services/rutas";
import { VacunaVencida } from "../../../services/analisisOperaciones";
import Pagination from "../../organisms/Pagination";
import { generatePage } from "../../../utils/tableUtils";
import { formatearFechaCortaStr } from "../../../utils/dateUtils";

interface VacunasVencidasTableProps {
  vacunasVencidas: VacunaVencida[];
  isLoading: boolean;
  itemsPorPagina: number;
}

function VacunasVencidasTable({
  vacunasVencidas,
  isLoading,
  itemsPorPagina,
}: VacunasVencidasTableProps) {
  const [activePageNumber, setActivePageNumber] = useState(1);
  const navigate = useNavigate();

  const paginaActual = useMemo(
    () =>
      generatePage(
        vacunasVencidas,
        activePageNumber,
        itemsPorPagina
      ) as VacunaVencida[],
    [vacunasVencidas, activePageNumber, itemsPorPagina]
  );
  const totalVacunasVencidas = vacunasVencidas.length;

  const formatearFechaVacuna = (fechaVacuna: string | null): string => {
    if (fechaVacuna === null) {
      return "N/A";
    }
    return formatearFechaCortaStr(fechaVacuna);
  };

  return (
    <>
      <Table responsive hover>
        <thead>
          <tr>
            <StyledColHeader>Mascota</StyledColHeader>
            <StyledColHeader>Propietario</StyledColHeader>
            <StyledColHeader>Fecha vacuna</StyledColHeader>
          </tr>
        </thead>
        <tbody>
          {paginaActual.map((item) => (
            <tr key={item.idMascota}>
              <td>
                <LinkButton
                  type="button"
                  onClick={() =>
                    navigate(`${RUTA_MASCOTAS}/${item.idMascota}`, { replace: false })
                  }
                >
                  {item.nombreMascota}
                </LinkButton>
              </td>
              <td>
                <LinkButton
                  type="button"
                  onClick={() =>
                    navigate(`${RUTA_PROPIETARIOS}/${item.idPropietario}`, {
                      replace: false,
                    })
                  }
                >
                  {item.propietario}
                </LinkButton>
              </td>
              <td>{formatearFechaVacuna(item.fechaVacuna)}</td>
            </tr>
          ))}
          {totalVacunasVencidas > 0 && (
            <StyledSummaryRow>
              <td colSpan={2}>Total</td>
              <td>{totalVacunasVencidas}</td>
            </StyledSummaryRow>
          )}
          {!isLoading && paginaActual.length === 0 && (
            <tr>
              <td colSpan={3}>No hay mascotas con vacunas vencidas actualmente.</td>
            </tr>
          )}
        </tbody>
      </Table>

      <Pagination
        pageSize={itemsPorPagina}
        setActivePageNumber={setActivePageNumber}
        activePageNumber={activePageNumber}
        rows={vacunasVencidas}
      />
    </>
  );
}

const StyledColHeader = styled("th")`
  color: ${(props) => props.theme.colors.main};
`;

const StyledSummaryRow = styled("tr")`
  background-color: ${(props) => props.theme.colors.light};
  font-weight: 700;
`;

const LinkButton = styled("button")`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(props) => props.theme.colors.main};
  text-decoration: underline;
`;

export default VacunasVencidasTable;
